import React, { useMemo } from "react";
import ReactApexChart from "react-apexcharts";

const IncidentPieChart = ({ categoryCount }) => {
  const COLORS = [
    '#FF9F40', // Volume (Orange)
    '#FF6384', // Freshness (Pink/Red)
    '#4BC0C0', // Schema Drift (Teal)
    '#36A2EB', // Field Health (Blue)
    '#9966FF', // Custom (Purple)
    '#FF6B6B', // Job Failure (Red)
    '#4ECDC4', // Accuracy (Light Teal)
    '#FFE66D', // Completeness (Yellow)
    '#95E1D3', // Consistency (Mint)
    '#F38181', // Validity (Light Red)
  ];

  // Convert categoryCount object to ApexCharts format
  const chartData = useMemo(() => {
    const entries = Object.entries(categoryCount)
      .filter(([name]) => name !== 'totalIncidents');
    
    return {
      series: entries.map(([, value]) => value),
      labels: entries.map(([name]) => name)
    };
  }, [categoryCount]);

  // Get total incidents from the object or calculate it
  const totalIncidents = useMemo(() => {
    return categoryCount.totalIncidents || 
      Object.entries(categoryCount)
        .filter(([name]) => name !== 'totalIncidents')
        .reduce((sum, [, value]) => sum + value, 0);
  }, [categoryCount]);

  // If no data, return null
  if (totalIncidents === 0) {
    return null;
  }

  const chartOptions = {
    chart: {
      type: 'pie',
    },
    labels: chartData.labels,
    colors: COLORS,
    legend: {
      show: false
    },
    dataLabels: {
      enabled: true,
      formatter: function (val) {
        return val.toFixed(0) + "%";
      }
    },
    tooltip: {
      y: {
        formatter: function(value) {
          const percentage = ((value / totalIncidents) * 100).toFixed(1);
          return `${value} (${percentage}%)`;
        }
      }
    },
    responsive: [{
      breakpoint: 1024,
      options: {
        chart: {
          width: 200,
          height: 200
        }
      }
    }, {
      breakpoint: 768,
      options: {
        chart: {
          width: 180,
          height: 180
        }
      }
    }, {
      breakpoint: 480,
      options: {
        chart: {
          width: 150,
          height: 150
        }
      }
    }]
  };

  return (
    <div className="bg-white rounded-lg border border-gray-300 p-6">
      <div className="flex items-start justify-between ">
        {/* Pie Chart */}
        <div className="flex items-center space-x-8">
          <div style={{ width: '230px', height: '230px' }}>
            <ReactApexChart 
              options={chartOptions} 
              series={chartData.series} 
              type="pie" 
              width={230}
              height={230}
            />
          </div>

          <div>
            <div className="text-sm text-gray-600 mb-1">Total Incidents</div>
            <div className="text-4xl font-bold text-gray-800">{totalIncidents}</div>
          </div>
        </div>

        {/* Legend/Stats */}
        <div className="grid grid-cols-3 gap-6 flex-1 ml-12">
          {Object.entries(categoryCount)
            .filter(([category]) => category !== 'totalIncidents')
            .map(([category, count], index) => {
              const percentage = ((count / totalIncidents) * 100).toFixed(0);
              return (
                <div key={category} className="flex items-center space-x-3">
                  <div 
                    className="w-[5px] h-[30px] rounded-full" 
                    style={{ backgroundColor: COLORS[index % COLORS.length] }}
                  ></div>
                  <div>
                    <div className="text-sm text-gray-600">{category} ({percentage}%)</div>
                    <div className="text-2xl font-bold text-gray-800">{count}</div>
                  </div>
                </div>
              );
            })}
        </div>
      </div>
    </div>
  );
};

export default IncidentPieChart;

